import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { FiTrendingUp, FiInfo, FiUsers, FiRefreshCw } from 'react-icons/fi'
import { 
  Container, 
  Card, 
  Grid, 
  Heading1, 
  Heading2, 
  BodyText, 
  SmallText, 
  Button, 
  StatusBadge,
  LoadingSpinner,
  Input 
} from '../components/ui'
import { ScatterChart, BarChart, createResilienceScatterData } from '../components/Chart'
import { theme, mediaQuery } from '../theme'
import { analyticsAPI, dataHelpers } from '../services/api'
import ResilienceTimeline from '../components/ResilienceTimeline'

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.xl};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`

const MetricIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  margin-bottom: ${theme.spacing.md};
`

const ScoreDisplay = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`

const MainScore = styled.div`
  font-size: 72px;
  font-weight: 700;
  font-family: ${theme.typography.heading.fontFamily};
  color: ${theme.colors.primary.main};
  line-height: 1;
  margin: ${theme.spacing.md} 0;
`

const ScoreInterpretation = styled.div`
  font-size: ${theme.typography.body.p1.fontSize};
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.md};
`

const InfoCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.primary.light}20, ${theme.colors.primary.main}10);
  border-left: 4px solid ${theme.colors.primary.main};
`

const UserAnalysisCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.primary.main}10, ${theme.colors.secondary.green}10);
  border: 2px solid ${theme.colors.primary.main}30;
`

const UserInputSection = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
  }
`

const ResiliencePage = () => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [apiResponse, setApiResponse] = useState(null)
  
  // Individual user analysis state
  const [selectedUserId, setSelectedUserId] = useState('')
  const [userAnalysis, setUserAnalysis] = useState(null)
  const [userScatterData, setUserScatterData] = useState(null)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [userError, setUserError] = useState(null)
  
  useEffect(() => {
    loadResilienceData()
  }, [])
  
  // Transform backend rankings data to frontend user structure
  const transformRankingsToUsers = (rankingsData) => {
    const { mentalResilienceRankings } = rankingsData
    
    // Create a map to combine all metrics for each user
    const userMap = new Map()
    
    // Add resilience data
    mentalResilienceRankings?.forEach(user => {
      if (!userMap.has(user.userId)) {
        userMap.set(user.userId, {
          userId: user.userId,
          entryCount: user.validEntries || 0,
          metrics: {}
        })
      }
      userMap.get(user.userId).metrics.resilience = {
        score: user.score,
        interpretation: user.interpretation
      }
    })
    
    return Array.from(userMap.values())
  }
  
  const loadResilienceData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      // First try to get rankings data
      console.log('🔍 Attempting to fetch rankings data...')
      let response
      let users = []
      let realScatterData = []
      
      try {
        response = await analyticsAPI.getRankings()
        setApiResponse(response) // Store for use in UI
        console.log('✅ Rankings API Response:', response) // Debug logging
        console.log('📊 Rankings data structure:', response.data) // Debug logging
        
        // Transform backend data structure to frontend expected structure
        const rankingsData = response.data
        if (rankingsData.mentalResilienceRankings) {
          // Backend returned the expected rankings structure
          users = transformRankingsToUsers(rankingsData)
          console.log('🔄 Transformed users:', users.length)
        } else {
          // Fallback for different structure
          users = response.data?.users || response.data || []
        }
        
        console.log('👥 Total users found:', users.length) // Debug logging
        
        if (users.length > 0) {
          console.log('📝 Sample user structure:', users[0]) // Debug logging
        }
        
        // Get real scatter plot data from all valid journal entries
        console.log('🔍 Fetching all real scatter plot data...')
        try {
          const allScatterResponse = await analyticsAPI.getAllScatterData()
          if (allScatterResponse.data.success && allScatterResponse.data.dataPoints) {
            realScatterData = allScatterResponse.data.dataPoints.map(point => ({
              pressure: point.x,
              emotionalScore: point.y,
              createdAt: point.date,
              userId: point.userId
            }))
            console.log('📊 Real scatter data points:', realScatterData.length)
            console.log('👥 From unique users:', allScatterResponse.data.statistics.uniqueUsers)
          } else {
            console.warn('⚠️ No scatter data available from comprehensive endpoint')
          }
        } catch (scatterError) {
          console.warn('⚠️ Failed to fetch comprehensive scatter data:', scatterError)
          
          // Fallback to individual user approach
          const resilientUsers = users.filter(user => user.metrics?.resilience?.score)
          if (resilientUsers.length > 0) {
            const sampleUsers = resilientUsers.slice(0, 10)
            const scatterPromises = sampleUsers.map(user => 
              analyticsAPI.getUserScatterData(user.userId).catch(err => {
                console.warn(`Failed to get scatter data for user ${user.userId}:`, err)
                return null
              })
            )
            
            const scatterResults = await Promise.all(scatterPromises)
            realScatterData = scatterResults
              .filter(result => result && result.dataPoints && result.dataPoints.length > 0)
              .flatMap(result => result.dataPoints.map(point => ({
                pressure: point.x,
                emotionalScore: point.y,
                createdAt: point.date,
                userId: result.userId
              })))
          }
        }
        
      } catch (rankingsError) {
        console.warn('⚠️ Rankings API failed, trying summary API:', rankingsError)
        
        // Fallback to summary API
        try {
          const summaryResponse = await analyticsAPI.getSummary()
          console.log('📈 Summary API Response:', summaryResponse.data)
          users = summaryResponse.data?.users || []
        } catch (summaryError) {
          console.error('❌ Both rankings and summary APIs failed')
          throw summaryError
        }
      }
      
      // Calculate overall resilience score
      const resilientUsers = users.filter(user => user.metrics?.resilience?.score)
      console.log('🎯 Users with resilience data:', resilientUsers.length) // Debug logging
      
      if (resilientUsers.length > 0) {
        console.log('🔬 Sample resilient user:', resilientUsers[0]) // Debug logging
        console.log('📊 Sample resilience score:', resilientUsers[0].metrics.resilience) // Debug logging
      } else if (users.length > 0) {
        console.log('⚠️ No resilience data found. Sample user metrics:', users[0].metrics)
      } else {
        console.log('⚠️ No users found at all. This likely means the analysis hasn\'t been run yet.')
      }
      
      const averageScore = resilientUsers.length > 0 
        ? resilientUsers.reduce((sum, user) => sum + user.metrics.resilience.score, 0) / resilientUsers.length
        : 0
      
      // Use real scatter data if available, otherwise fall back to sample data for visualization
      const entriesForVisualization = realScatterData.length > 0 ? realScatterData : generateSamplePressureData(50)
      
      setData({
        averageScore,
        users: resilientUsers,
        sampleEntries: entriesForVisualization,
        totalUsers: response?.data?.totalUsers || users.length,
        usingRealData: realScatterData.length > 0,
        realDataPoints: realScatterData.length
      })
    } catch (err) {
      console.error('❌ Complete API failure:', err)
      const errorMsg = err.response?.status === 404 
        ? 'Backend API endpoint not found. Make sure the backend server is running on port 8080.'
        : 'Failed to load resilience data. Check the browser console for details.'
      setError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }
  
  // Generate sample pressure vs emotion data for visualization
  const generateSamplePressureData = (count) => {
    const entries = []
    for (let i = 0; i < count; i++) {
      const pressure = Math.random() * 10
      // Simulate different pressure vs emotion responses
      const baseEmotion = 5 + Math.random() * 3
      const pressureEffect = Math.random() > 0.7 
        ? pressure * 0.3 // Some people get better under pressure
        : -pressure * 0.5 // Most people get worse under pressure
      const emotionalScore = Math.max(0, Math.min(10, baseEmotion + pressureEffect + (Math.random() - 0.5)))
      
      entries.push({
        pressure: Math.round(pressure * 10) / 10,
        emotionalScore: Math.round(emotionalScore * 10) / 10,
        createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      })
    }
    return entries.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
  }
  

  
  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner />
        <BodyText style={{ textAlign: 'center' }}>Loading resilience analysis...</BodyText>
      </Container>
    )
  }
  
  if (error) {
    return (
      <Container>
        <Card style={{ textAlign: 'center', background: theme.colors.status.error, color: 'white' }}>
          <Heading2 style={{ color: 'white', marginBottom: theme.spacing.md }}>
            Error Loading Data
          </Heading2>
          <BodyText style={{ color: 'white', marginBottom: theme.spacing.lg }}>
            {error}
          </BodyText>
          <Button variant="outline" onClick={loadResilienceData} style={{ color: 'white', borderColor: 'white' }}>
            Try Again
          </Button>
        </Card>
      </Container>
    )
  }
  
  const { score, interpretation } = dataHelpers.formatScore(data.averageScore, 'resilience')
  const status = dataHelpers.getStatusCategory(score)
  
  // Function to create user-averaged scatter data
  const createUserAveragedScatterData = (entries) => {
    if (!entries || entries.length === 0) return { datasets: [] }
    
    // Group entries by userId
    const userGroups = entries.reduce((groups, entry) => {
      const userId = entry.userId
      if (!groups[userId]) {
        groups[userId] = []
      }
      groups[userId].push(entry)
      return groups
    }, {})
    
    // Calculate averages for each user
    const userAverages = Object.entries(userGroups).map(([userId, userEntries]) => {
      const avgPressure = userEntries.reduce((sum, entry) => sum + entry.pressure, 0) / userEntries.length
      const avgEmotional = userEntries.reduce((sum, entry) => sum + entry.emotionalScore, 0) / userEntries.length
      
      return {
        x: avgPressure,
        y: avgEmotional,
        userId: userId,
        entryCount: userEntries.length
      }
    })
    
    return {
      datasets: [{
        label: `Average Pressure vs Emotional Response (${userAverages.length} users)`,
        data: userAverages,
        backgroundColor: theme.colors.primary.main + '80',
        borderColor: theme.colors.primary.main,
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        // Custom tooltip callback to show user info
        tooltip: {
          callbacks: {
            afterLabel: function(context) {
              const point = context.raw
              return `User: ${point.userId}\nEntries: ${point.entryCount}`
            }
          }
        }
      }]
    }
  }
  
  // Create user-averaged chart data
  const scatterData = createUserAveragedScatterData(data.sampleEntries)
  
  // Create distribution data
  const distributionCounts = [
    data.users.filter(u => u.metrics.resilience.score >= 85).length,
    data.users.filter(u => u.metrics.resilience.score >= 50 && u.metrics.resilience.score < 85).length,
    data.users.filter(u => u.metrics.resilience.score >= 25 && u.metrics.resilience.score < 50).length,
    data.users.filter(u => u.metrics.resilience.score < 25).length,
  ]
  
  // Calculate failed calculations: users who meet minimum requirements but don't have scores
  const usersWithScores = data.users.filter(u => u.metrics?.resilience?.score !== null && u.metrics?.resilience?.score !== undefined).length
  const failedCalculations = Math.max(0, data.totalUsers - usersWithScores)
  
  // Add failed calculations to the data
  const allDistributionCounts = [...distributionCounts, failedCalculations]
  
  console.log('Distribution counts:', distributionCounts) // Debug logging
  console.log(`Users with scores: ${usersWithScores}, Users with min data: ${data.totalUsers}, Failed calculations: ${failedCalculations}`)
  
  const distributionData = {
    labels: ['Superhuman (85+)', 'Moderately Affected (50-84)', 'Notably Decreased (25-49)', 'Very Poor (0-24)', 'Failed Calculation'],
    datasets: [{
      label: 'Number of Users',
      data: allDistributionCounts,
      backgroundColor: [
        theme.colors.status.success,
        theme.colors.primary.main,
        theme.colors.status.warning,
        theme.colors.primary.dark,
        theme.colors.status.warning + '60', // Semi-transparent warning color for failed calculations
      ],
      borderColor: [
        theme.colors.status.success,
        theme.colors.primary.main,
        theme.colors.status.warning,
        theme.colors.primary.dark,
        theme.colors.status.warning,
      ],
      borderWidth: 2,
    }],
  }
  
  const hasResilienceData = data.users.length > 0
  
  // Handle individual user analysis
  const handleUserAnalysis = async () => {
    if (!selectedUserId.trim()) return
    
    setIsLoadingUser(true)
    setUserAnalysis(null)
    setUserScatterData(null)
    setUserError(null)
    
    try {
      // Fetch both analysis and scatter data
      const [analysisResponse, scatterResponse] = await Promise.all([
        analyticsAPI.getUserAnalysis(selectedUserId.trim()),
        analyticsAPI.getUserScatterData(selectedUserId.trim())
      ])
      
      console.log('Individual user analysis:', analysisResponse.data)
      console.log('Individual user scatter data:', scatterResponse.data)
      
      if (analysisResponse.data.mentalResilience && analysisResponse.data.mentalResilience.score !== null) {
        setUserAnalysis(analysisResponse.data)
        
        if (scatterResponse.data.dataPoints && scatterResponse.data.dataPoints.length > 0) {
          setUserScatterData(scatterResponse.data)
        }
      } else {
        setUserError(`User ${selectedUserId} has insufficient data for resilience analysis. Need at least 2 entries with both pressure and emotional factors.`)
      }
    } catch (err) {
      setUserError(`Failed to analyze user ${selectedUserId}: ${err.message}`)
    } finally {
      setIsLoadingUser(false)
    }
  }
  
  return (
    <Container>
      <PageHeader>
        <div>
          <MetricIcon>
            <FiTrendingUp />
          </MetricIcon>
          <Heading1>Mental Resilience Analysis</Heading1>
          <BodyText>
            How emotions respond to pressure across all users
          </BodyText>
        </div>
        
        <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
          <Button onClick={loadResilienceData} variant="outline">
            <FiRefreshCw style={{ marginRight: '8px' }} />
            Refresh Data
          </Button>
          
          <Button 
            onClick={async () => {
              console.log('🧪 Testing API endpoints directly...')
              try {
                const health = await analyticsAPI.healthCheck()
                console.log('✅ Health check:', health.data)
                
                const rankings = await analyticsAPI.getRankings()
                console.log('✅ Rankings response:', rankings.data)
                
                const summary = await analyticsAPI.getSummary()
                console.log('✅ Summary response:', summary.data)
              } catch (err) {
                console.error('❌ API test failed:', err)
                alert('API test failed - check console for details')
              }
            }}
            variant="secondary"
          >
            🧪 Test APIs
          </Button>
        </div>
      </PageHeader>
      
      {/* Overall Score */}
      <ScoreDisplay>
        {hasResilienceData ? (
          <>
            <MainScore>{score}</MainScore>
            <StatusBadge status={status} style={{ fontSize: '16px', padding: '8px 16px' }}>
              {interpretation}
            </StatusBadge>
            <ScoreInterpretation>
              Average across {data.users.length} users with sufficient data
            </ScoreInterpretation>
          </>
        ) : (
          <>
            <MainScore style={{ color: theme.colors.primary.light }}>--</MainScore>
            <StatusBadge status="fair" style={{ fontSize: '16px', padding: '8px 16px', background: theme.colors.primary.light }}>
              No data available
            </StatusBadge>
            <ScoreInterpretation>
              Run analysis to calculate resilience metrics for {data.totalUsers} total users
            </ScoreInterpretation>
          </>
        )}
      </ScoreDisplay>
      
      {/* Charts */}
      <Grid columns="1fr" desktopColumns="1fr 1fr">
        <Card>
          <Heading2>Pressure vs Emotional Response</Heading2>
          <BodyText>
            Each point represents one user's average pressure and emotional response across all their journal entries. 
            Points in the upper right show users who maintain high emotions under high pressure (Superhuman).
          </BodyText>
          
          {/* Data Source Information */}
          {data && (
            <div style={{ 
              padding: theme.spacing.sm, 
              background: data.usingRealData ? theme.colors.status.success + '20' : theme.colors.status.warning + '20',
              borderRadius: theme.borderRadius.sm,
              border: `1px solid ${data.usingRealData ? theme.colors.status.success : theme.colors.status.warning}50`,
              marginBottom: theme.spacing.md
            }}>
              <SmallText style={{ 
                color: data.usingRealData ? theme.colors.status.success : theme.colors.status.warning,
                margin: 0,
                fontWeight: 500
              }}>
                {data.usingRealData 
                  ? `📊 Real Data: User averages calculated from ${data.realDataPoints} actual journal entries`
                  : '⚠️ Sample Data: Using simulated user averages (insufficient real data available)'
                }
              </SmallText>
            </div>
          )}
          
          <ScatterChart 
            data={scatterData} 
            height={350}
            title="User Average Analysis"
          />
        </Card>
        
        <Card>
          <Heading2>Resilience Distribution</Heading2>
          <BodyText>
            Distribution of mental resilience scores across users in the dataset. 
            Shows calculated scores plus users who meet minimum requirements (2+ entries) but failed score calculation.
          </BodyText>
          {hasResilienceData ? (
            <>
              <BarChart 
                data={distributionData} 
                height={350}
                title="User Resilience Levels"
              />
              
              {/* Explanation for Failed Calculation bar */}
              {failedCalculations > 0 && (
                <div style={{ 
                  padding: theme.spacing.md, 
                  background: theme.colors.status.warning + '15',
                  borderRadius: theme.borderRadius.md,
                  margin: `${theme.spacing.md} 0`,
                  border: `1px solid ${theme.colors.status.warning}40`
                }}>
                  <SmallText style={{ color: theme.colors.secondary.black, marginBottom: theme.spacing.sm }}>
                    <strong>📊 About "Failed Calculation":</strong>
                  </SmallText>
                  <SmallText style={{ color: theme.colors.secondary.black, marginBottom: theme.spacing.sm }}>
                    {failedCalculations} users have 2+ journal entries (minimum requirement) but their resilience score calculation failed.
                    This typically happens when pressure or emotional values lack sufficient variance for linear regression analysis.
                  </SmallText>
                  <SmallText style={{ color: theme.colors.primary.main }}>
                    💡 These users are included in the <strong>Resilience Pattern Analysis</strong> above using simpler average-based calculations.
                  </SmallText>
                </div>
              )}
            </>
          ) : (
             <div style={{ 
               textAlign: 'center', 
               padding: theme.spacing.xl, 
               background: theme.colors.status.warning + '20',
               borderRadius: theme.borderRadius.md,
               margin: `${theme.spacing.md} 0`,
               border: `2px solid ${theme.colors.status.warning}50`
             }}>
               <BodyText style={{ color: theme.colors.secondary.black, marginBottom: theme.spacing.md }}>
                 ⚠️ No Mental Resilience Data Available
               </BodyText>
               <SmallText style={{ color: theme.colors.secondary.black, marginBottom: theme.spacing.sm }}>
                 <strong>Resilience calculation requires both PRESSURE and EMOTIONAL scores.</strong>
               </SmallText>
               <SmallText style={{ color: theme.colors.secondary.black, marginBottom: theme.spacing.sm }}>
                 Your dataset has {data.totalUsers} users but insufficient pressure data coverage.
               </SmallText>
               <SmallText style={{ color: theme.colors.primary.light }}>
                 💡 Try the <strong>Resilience</strong> page for available metrics.
               </SmallText>
             </div>
           )}
        </Card>
      </Grid>
      
      {/* Data Quality Card */}
      {!hasResilienceData && (
        <Card style={{ background: theme.colors.status.info + '20', border: `2px solid ${theme.colors.status.info}50` }}>
          <Heading2 style={{ color: theme.colors.primary.main, marginBottom: theme.spacing.md }}>
            📊 Data Quality Information
          </Heading2>
          <Grid columns="repeat(auto-fit, minmax(200px, 1fr))">
            <div>
              <BodyText style={{ marginBottom: theme.spacing.sm }}>
                <strong>Total Users:</strong> {data.totalUsers}
              </BodyText>
              <BodyText style={{ marginBottom: theme.spacing.sm }}>
                <strong>With Emotional Data:</strong> {data.users.filter(u => u.metrics?.resilience?.score).length}
              </BodyText>
              <BodyText style={{ marginBottom: theme.spacing.sm }}>
                <strong>With Resilience Data:</strong> {data.users.length} (0%)
              </BodyText>
            </div>
            <div>
              <BodyText style={{ marginBottom: theme.spacing.sm }}>
                <strong>Available Metrics:</strong>
              </BodyText>
              <SmallText style={{ marginBottom: theme.spacing.xs }}>
                ❌ Mental Resilience (requires pressure data)
              </SmallText>
            </div>
          </Grid>
        </Card>
      )}

      {/* Individual User Analysis */}
      <UserAnalysisCard>
        <Heading2 style={{ marginBottom: theme.spacing.md }}>
          Individual User Analysis
        </Heading2>
        <BodyText style={{ marginBottom: theme.spacing.md }}>
          Analyze the pressure vs emotional response pattern for a specific user to see their individual resilience profile.
        </BodyText>
        
        <UserInputSection>
          <Input
            placeholder="Enter User ID (e.g., S32IhVNLPkRrZUzWPcDyYPCHIgY2)"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button 
            onClick={handleUserAnalysis}
            disabled={isLoadingUser || !selectedUserId.trim()}
          >
            {isLoadingUser ? <LoadingSpinner style={{ width: '16px', height: '16px', margin: '0' }} /> : 'Analyze User'}
          </Button>
        </UserInputSection>
        
        {userError && (
          <div style={{ 
            padding: theme.spacing.md, 
            background: theme.colors.status.error + '20',
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.status.error}50`,
            marginBottom: theme.spacing.md
          }}>
            <BodyText style={{ color: theme.colors.status.error, margin: 0 }}>
              {userError}
            </BodyText>
          </div>
        )}
        
        {userAnalysis && (
          <div>
            <Grid columns="repeat(auto-fit, minmax(200px, 1fr))" style={{ marginBottom: theme.spacing.lg }}>
              <div style={{ textAlign: 'center' }}>
                <MainScore style={{ color: theme.colors.primary.main }}>
                  {Math.round(userAnalysis.mentalResilience.score * 100) / 100}
                </MainScore>
                <ScoreInterpretation>
                  {userAnalysis.mentalResilience.interpretation}
                </ScoreInterpretation>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <BodyText style={{ margin: 0 }}>
                  <strong>User:</strong> {userAnalysis.userId}
                </BodyText>
                <BodyText style={{ margin: 0 }}>
                  <strong>Data Points:</strong> {userAnalysis.mentalResilience.dataPoints} entries
                </BodyText>
                <BodyText style={{ margin: 0 }}>
                  <strong>R-squared:</strong> {Math.round(userAnalysis.mentalResilience.rSquared * 1000) / 1000}
                </BodyText>
                <BodyText style={{ margin: 0 }}>
                  <strong>Slope:</strong> {Math.round(userAnalysis.mentalResilience.slope * 1000) / 1000}
                </BodyText>
              </div>
            </Grid>
            
            {/* User-specific scatter plot */}
            <div style={{ marginTop: theme.spacing.lg }}>
              <Heading2 style={{ marginBottom: theme.spacing.md }}>
                Individual Pressure vs Emotional Response Pattern
              </Heading2>
              {userScatterData ? (
                <div>
                  <ScatterChart
                    data={{
                      datasets: [{
                        label: `${userAnalysis.userId} - Emotional Response`,
                        data: userScatterData.dataPoints,
                        backgroundColor: theme.colors.primary.main + '80',
                        borderColor: theme.colors.primary.main,
                        borderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                      }]
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Pressure vs Emotional Response (Individual User)',
                          font: { size: 16, weight: 'bold' }
                        },
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Pressure Level (0-10)',
                            font: { size: 14, weight: 'bold' }
                          },
                          min: 0,
                          max: 10,
                          grid: { color: theme.colors.primary.light + '30' }
                        },
                        y: {
                          title: {
                            display: true,
                            text: 'Emotional Response (0-10)',
                            font: { size: 14, weight: 'bold' }
                          },
                          min: 0,
                          max: 10,
                          grid: { color: theme.colors.primary.light + '30' }
                        }
                      }
                    }}
                  />
                  <div style={{ 
                    marginTop: theme.spacing.md,
                    padding: theme.spacing.md,
                    background: theme.colors.secondary.beige,
                    borderRadius: theme.borderRadius.md
                  }}>
                    <SmallText style={{ color: theme.colors.primary.main }}>
                      <strong>Chart Insights:</strong> Each point represents one journal entry. 
                      {userAnalysis.mentalResilience.slope > 0 
                        ? "📈 This user's emotional response tends to improve under pressure."
                        : "📉 This user's emotional response tends to decline under pressure."
                      } (Slope: {Math.round(userAnalysis.mentalResilience.slope * 1000) / 1000})
                    </SmallText>
                  </div>
                </div>
              ) : (
                <div style={{ 
                  padding: theme.spacing.lg,
                  background: theme.colors.secondary.beige,
                  borderRadius: theme.borderRadius.md,
                  textAlign: 'center'
                }}>
                  <BodyText style={{ color: theme.colors.primary.light }}>
                    No scatter plot data available for this user.
                  </BodyText>
                </div>
              )}
            </div>

            {/* Resilience Timeline */}
            <ResilienceTimeline userId={selectedUserId} />
          </div>
        )}
      </UserAnalysisCard>

      {/* Insights */}
      <InfoCard>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.md }}>
          <FiInfo style={{ fontSize: '24px', color: theme.colors.primary.main, marginTop: '4px' }} />
          <div>
            <Heading2 style={{ marginBottom: theme.spacing.sm }}>About Mental Resilience</Heading2>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>Mental Resilience Score:</strong> Calculated using linear regression of emotions vs pressure
            </BodyText>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>Score Range:</strong> 0-100, where higher scores indicate better resilience under pressure
            </BodyText>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>Interpretation:</strong> Users with scores above 75 maintain mental resilience under pressure
            </BodyText>
            <BodyText style={{ marginBottom: '0' }}>
              • <strong>Data Requirements:</strong> Minimum 2 journal entries with BOTH pressure + emotional factors
            </BodyText>
          </div>
        </div>
      </InfoCard>
    </Container>
  )
}

export default ResiliencePage 